import { forwardRef, useState } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * PopOverコンポーネント内のオプションを表します。
 */
interface Option {
  /** ラベル。 */
  label: string;
  /** 強調表示するかどうか。 */
  highlight?: boolean;
  /** actionが無効かどうか。 */
  disabled?: boolean;
  /** クリックされたときに実行されるアクション。 */
  action: () => void;
}

/**
 * PopOverコンポーネントのプロパティ。
 */
interface PopOverProps {
  /** ポップオーバーに表示するオプションのリスト。 */
  options: Option[];
  /** ポップオーバートリガーボタンのラベル。 */
  label?: string;
  /**
   * コンテンツの水平アライメント。既定は `start`。
   * 画面右端の行アクション等では `end` を指定してビューポート右端のはみ出し/見切れを防ぐ。
   */
  align?: 'start' | 'center' | 'end';
}

/**
 * オプションのリストをポップオーバー内に表示するPopOverコンポーネント。
 *
 * @param {PopOverProps} props - コンポーネントのプロパティ。
 * @returns {JSX.Element} レンダリングされたPopOverコンポーネント。
 */
const PopOver = forwardRef<HTMLDivElement, PopOverProps>(({
  options,
  label,
  align = 'start'
}, ref) => {
  const [open, setOpen] = useState(false);
  return <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {label ? <Button variant="outline" aria-label={label}>
              {label}
            </Button> : <Button variant="ghost" size="icon" aria-label="Options">
              <Icon iconName="MoreHorizontalIcon" className="h-5 w-5" />
            </Button>}
        </PopoverTrigger>
        <PopoverContent ref={ref} align={align}
    // w-max で最長ラベルにフィット。whitespace-normal だと w-full 子との循環で
    // max-w-sm まで広がって右余白が空くため、ラベルは nowrap のままにする。
    className="flex w-max max-w-sm flex-col p-2">
          {options.map(option => <Button key={option.label} onClick={() => {
        option.action();
        setOpen(false);
      }} disabled={option.disabled} variant="ghost" className={cn('w-full justify-start', option.highlight && 'text-error-OnNeutral')}>
              {option.label}
            </Button>)}
        </PopoverContent>
      </Popover>;
});
PopOver.displayName = 'PopOver';
export default PopOver;